import { Button } from '@/components/ui/button';
import { CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CreditCard, DollarSign } from 'lucide-react';
import ZohoBillingButton from '@/components/ZohoBillingButton';
import { fireEvent } from '@/lib/analytics';
import { toast } from '@/hooks/use-toast';
import { useParallaxRef } from '@/utils/parallax';

interface UserAsset {
  relationship: 'LEASE' | 'BIN' | 'LEAD';
  billing_status: string;
}

interface BillingTabProps {
  stripeCustomerId: string | null;
  assets: UserAsset[];
  userEmail?: string | null;
  isEmailVerified: boolean;
  onEarlyPayoffClick: () => void;
}

export default function BillingTab({
  stripeCustomerId,
  assets,
  userEmail,
  isEmailVerified,
  onEarlyPayoffClick,
}: BillingTabProps) {
  const handleManageBilling = async () => {
    const customerId = stripeCustomerId || new URLSearchParams(window.location.search).get('cid');
    if (customerId && customerId.startsWith('cus_')) {
      fireEvent('billing_portal_clicked', { origin: 'account' });
      try {
        const response = await fetch('/api/stripe/portal', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customerId }),
        });
        const data = await response.json();
        if (data.ok && data.data?.url) {
          window.location.href = data.data.url;
        } else {
          throw new Error(data.message || 'Failed to create portal session');
        }
      } catch (error) {
        console.error('[account] Billing portal error:', error);
        toast({
          title: 'Error',
          description: 'Could not open billing portal. Please try again or contact support.',
          variant: 'destructive',
        });
      }
    } else {
      toast({
        title: 'No billing account',
        description: 'Complete a purchase first to access billing portal.',
      });
    }
  };

  const subtitleRef = useParallaxRef({ speed: -0.10, axis: 'y', maxTranslate: 32 });

  return (
    <>
      <CardHeader className="text-center pb-12">
        <CardTitle className="text-4xl md:text-5xl font-bold mb-6">Billing & Payments</CardTitle>
        <CardDescription ref={subtitleRef as any} className="text-xl md:text-2xl text-foreground/70 mb-20">
          Manage your payment methods, view invoices, and update billing information
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 pt-4">
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {!isEmailVerified ? (
              <>
                <Button onClick={handleManageBilling} variant="default" disabled title="Verify your email to unlock billing actions">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Manage Billing
                </Button>
                {assets.some((a) => a.relationship === 'LEASE' && a.billing_status === 'active') && (
                  <Button onClick={onEarlyPayoffClick} variant="outline" disabled title="Verify your email to unlock billing actions">
                    <DollarSign className="w-4 h-4 mr-2" />
                    Early Payoff
                  </Button>
                )}
                <p className="text-sm text-muted-foreground w-full">
                  Verify your email to unlock billing actions.
                </p>
              </>
            ) : (
              <>
                <Button onClick={handleManageBilling} variant="default">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Manage Billing
                </Button>
                {/* Early Payoff CTA - only for active LTO */}
                {assets.some((a) => a.relationship === 'LEASE' && a.billing_status === 'active') && (
                  <Button onClick={onEarlyPayoffClick} variant="outline">
                    <DollarSign className="w-4 h-4 mr-2" />
                    Early Payoff
                  </Button>
                )}
                <ZohoBillingButton source="account" userEmail={userEmail || ''} />
              </>
            )}
          </div>
        </div>
      </CardContent>
    </>
  );
}

